import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Github, ExternalLink, BookOpen, Users, GitFork } from 'lucide-react';
interface GitHubUser {
  avatar_url: string;
  name: string | null;
  login: string;
  bio: string | null;
  public_repos: number;
  followers: number;
  following: number;
  html_url: string;
}
interface GitHubRepo {
  id: number;
  name: string;
  description: string | null;
  html_url: string;
  stargazers_count: number;
  forks_count: number;
  language: string | null;
}
const LANGUAGE_COLORS: Record<string, string> = {
  JavaScript: '#f1e05a',
  TypeScript: '#3178c6',
  HTML: '#e34c26',
  CSS: '#563d7c',
  Python: '#3572A5',
  Java: '#b07219',
  PHP: '#4F5D95',
  Ruby: '#701516',
  Go: '#00ADD8',
  Rust: '#dea584',
  Vue: '#41b883',
  SCSS: '#c6538c'
};
export function TestimonialsSection() {
  const [githubUser, setGithubUser] = useState<GitHubUser | null>(null);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    async function fetchGitHub() {
      try {
        const [userRes, reposRes] = await Promise.all([fetch('https://api.github.com/users/sagestephens'), fetch('https://api.github.com/users/sagestephens/repos?sort=updated&per_page=6')]);
        if (userRes.ok) setGithubUser(await userRes.json());
        if (reposRes.ok) setRepos(await reposRes.json());
      } catch {
        // Silently fail — card will show fallback
      } finally {
        setLoading(false);
      }
    }
    fetchGitHub();
  }, []);
  return <section id="testimonials" className="py-24 bg-dark relative" data-id="element-197">
      <div className="max-w-[1320px] mx-auto px-4" data-id="element-198">
        {/* GitHub Profile */}
        <div className="text-center mb-16" data-id="element-199">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-white font-syne mb-4" data-id="element-200">
            My <span className="text-gold" data-id="element-201">GitHub</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-white/60 font-inter max-w-lg mx-auto" data-id="element-202">
            Check out my open source work and contributions.
          </motion.p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto" data-id="element-203">
          {loading ? <div className="bg-dark-lighter rounded-2xl border border-white/5 p-12 flex items-center justify-center" data-id="element-204">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" data-id="element-205" />
            </div> : githubUser ? <div className="bg-dark-lighter rounded-2xl border border-white/5 overflow-hidden" data-id="element-206">
              {/* Profile Header */}
              <div className="p-8 md:p-10 border-b border-white/5" data-id="element-207">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6" data-id="element-208">
                  <img src={githubUser.avatar_url} alt={githubUser.name || githubUser.login} className="w-24 h-24 rounded-full border-2 border-gold shrink-0" data-id="element-209" />
                  <div className="text-center sm:text-left flex-1" data-id="element-210">
                    <h3 className="text-2xl font-bold text-white font-syne mb-1" data-id="element-211">
                      {githubUser.name || githubUser.login}
                    </h3>
                    <p className="text-gold font-inter text-sm mb-3" data-id="element-212">
                      @{githubUser.login}
                    </p>
                    {githubUser.bio && <p className="text-white/60 font-inter text-sm leading-relaxed mb-4" data-id="element-213">
                        {githubUser.bio}
                      </p>}
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6" data-id="element-214">
                      <div className="flex items-center gap-2 text-white/50 font-inter text-sm" data-id="element-215">
                        <BookOpen className="w-4 h-4" data-id="element-216" />
                        <span className="text-white font-semibold" data-id="element-217">
                          {githubUser.public_repos}
                        </span>{' '}
                        repos
                      </div>
                      <div className="flex items-center gap-2 text-white/50 font-inter text-sm" data-id="element-218">
                        <Users className="w-4 h-4" data-id="element-219" />
                        <span className="text-white font-semibold" data-id="element-220">
                          {githubUser.followers}
                        </span>{' '}
                        followers
                      </div>
                      <div className="flex items-center gap-2 text-white/50 font-inter text-sm" data-id="element-221">
                        <Users className="w-4 h-4" data-id="element-222" />
                        <span className="text-white font-semibold" data-id="element-223">
                          {githubUser.following}
                        </span>{' '}
                        following
                      </div>
                    </div>
                  </div>
                  <a href={githubUser.html_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-true-white font-inter font-semibold text-sm rounded-lg hover:bg-gold/90 transition-colors shrink-0" data-id="element-224">
                    <Github className="w-4 h-4" data-id="element-225" />
                    View Profile
                  </a>
                </div>
              </div>

              {/* Recent Repos */}
              {repos.length > 0 && <div className="p-8 md:p-10" data-id="element-226">
                  <h4 className="text-white font-syne font-bold text-lg mb-6" data-id="element-227">
                    Recent Repositories
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-228">
                    {repos.map(repo => <a key={repo.id} href={repo.html_url} target="_blank" rel="noopener noreferrer" className="group bg-dark p-5 rounded-xl border border-white/5 hover:border-gold/30 transition-all duration-300" data-id="element-229">
                        <div className="flex items-start justify-between mb-2" data-id="element-230">
                          <h5 className="text-white font-inter font-semibold text-sm group-hover:text-gold transition-colors truncate mr-2" data-id="element-231">
                            {repo.name}
                          </h5>
                          <ExternalLink className="w-3.5 h-3.5 text-white/30 group-hover:text-gold shrink-0 transition-colors" data-id="element-232" />
                        </div>
                        {repo.description && <p className="text-white/40 font-inter text-xs leading-relaxed mb-3 line-clamp-2" data-id="element-233">
                            {repo.description}
                          </p>}
                        <div className="flex items-center gap-4" data-id="element-234">
                          {repo.language && <span className="flex items-center gap-1.5 text-white/50 font-inter text-xs" data-id="element-235">
                              <span className="w-2.5 h-2.5 rounded-full" style={{
                      backgroundColor: LANGUAGE_COLORS[repo.language] || '#8b8b8b'
                    }} data-id="element-236" />
                              {repo.language}
                            </span>}
                          {repo.stargazers_count > 0 && <span className="flex items-center gap-1 text-white/50 font-inter text-xs" data-id="element-237">
                              <Star className="w-3 h-3" data-id="element-238" />
                              {repo.stargazers_count}
                            </span>}
                          {repo.forks_count > 0 && <span className="flex items-center gap-1 text-white/50 font-inter text-xs" data-id="element-239">
                              <GitFork className="w-3 h-3" data-id="element-240" />
                              {repo.forks_count}
                            </span>}
                        </div>
                      </a>)}
                  </div>
                </div>}
            </div> : <a href="https://github.com/sagestephens" target="_blank" rel="noopener noreferrer" className="block bg-dark-lighter rounded-2xl border border-white/5 p-12 text-center hover:border-gold/30 transition-colors" data-id="element-241">
              <Github className="w-16 h-16 text-gold mx-auto mb-4" data-id="element-242" />
              <h3 className="text-xl font-bold text-white font-syne mb-2" data-id="element-243">
                @sagestephens
              </h3>
              <p className="text-white/60 font-inter text-sm" data-id="element-244">
                View my GitHub profile →
              </p>
            </a>}
        </motion.div>
      </div>
    </section>;
}